import axios, { type InternalAxiosRequestConfig } from "axios";

import useToastStore from "@/store/toastStore";
import { savePostLoginRedirect } from "@/utils/postLoginRedirect";

export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// ---- 중복 요청 방지 ----
const pendingRequests = new Map<string, AbortController>();

// 요청 인터셉터에서 계산한 중복 방지 키와 재시도 횟수를 config 에 실어 둔다.
type TrackedConfig = InternalAxiosRequestConfig & {
  _requestKey?: string;
  _retryCount?: number;
};

function getRequestKey(config: InternalAxiosRequestConfig): string {
  const data = config.data ? JSON.stringify(config.data) : "";
  const params = config.params ? JSON.stringify(config.params) : "";

  return `${config.method}:${config.url}:${data}:${params}`;
}

// 응답 시점에는 config.data 가 직렬화된 문자열로 바뀌어 있어 getRequestKey 를
// 다시 호출하면 요청 시점과 다른 키가 나온다. 그러면 변경 요청의 키가 영원히
// 남아 같은 요청을 다시 보낼 때 "중복"으로 취소되므로, 요청 시점의 키를 재사용한다.
function getTrackedKey(config: TrackedConfig | undefined): string | undefined {
  if (!config) return undefined;
  return config._requestKey ?? getRequestKey(config);
}

function isReadRequest(config: InternalAxiosRequestConfig): boolean {
  if (config.method === "get") return true;
  if (
    config.method === "post" &&
    (config.url?.includes("/list/page") ||
      config.url?.includes("/my") ||
      config.url?.includes("/batch/preview"))
  )
    return true;
  return false;
}

// Request interceptor: JWT 토큰 + 중복/debounce 처리
axiosInstance.interceptors.request.use((config: TrackedConfig) => {
  try {
    const raw = localStorage.getItem("loginStorage");
    if (raw) {
      const parsed = JSON.parse(raw);
      const token = parsed?.state?.spotOnToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch {
    // localStorage 접근 실패 시 무시
  }

  // 503 재시도로 같은 config 가 다시 들어오면 기존 키를 유지한다.
  const key = config._requestKey ?? getRequestKey(config);
  config._requestKey = key;

  if (isReadRequest(config)) {
    // 조회 요청: 동일 요청이 짧은 시간 내에 오면 이전 요청 취소 (마지막만 실행)
    const existingController = pendingRequests.get(key);
    if (existingController) {
      existingController.abort();
    }
    const controller = new AbortController();
    config.signal = controller.signal;
    pendingRequests.set(key, controller);
  } else {
    // 변경 요청: 동일 요청이 진행 중이면 새 요청 무시 (첫 번째만 수용)
    if (pendingRequests.has(key)) {
      const controller = new AbortController();
      controller.abort();
      config.signal = controller.signal;
      return config;
    }
    const controller = new AbortController();
    config.signal = controller.signal;
    pendingRequests.set(key, controller);
  }

  return config;
});

// Response interceptor: 401 처리 + 503 자동 재시도 + pending 정리
axiosInstance.interceptors.response.use(
  response => {
    const key = getTrackedKey(response.config);
    if (key) pendingRequests.delete(key);
    return response;
  },
  async error => {
    const config: TrackedConfig | undefined = error.config;

    // 중복/debounce로 취소된 요청 → 조용히 무시
    if (axios.isCancel(error)) {
      return new Promise(() => {});
    }

    // pending 정리
    const key = getTrackedKey(config);
    if (key) pendingRequests.delete(key);

    // 401: 로그아웃 처리
    if (error.response?.status === 401) {
      try {
        localStorage.removeItem("loginStorage");
      } catch {
        // ignore
      }
      savePostLoginRedirect(window.location.pathname + window.location.search);
      window.location.href = "/login/main";
      return Promise.reject(error);
    }

    // 503: 서버 과부하 → 자동 재시도 (최대 3회)
    if (error.response?.status === 503 && config) {
      const retryCount = config._retryCount ?? 0;
      if (retryCount < 3) {
        config._retryCount = retryCount + 1;
        const retryAfter = (error.response.data?.retryAfter ?? 3) * 1000;
        await new Promise(resolve => setTimeout(resolve, retryAfter));
        return axiosInstance(config);
      }
    }

    // 네트워크 단절/타임아웃 또는 서버 오류(5xx)는 전역 토스트로 알린다.
    // API 함수들이 실패 시 빈 배열을 반환해 화면이 "데이터 없음"처럼 보이는 것을 보완.
    // (4xx 비즈니스 오류는 호출한 화면에서 개별 처리)
    if (!error.response || error.response.status >= 500) {
      useToastStore
        .getState()
        .showToast("서버와 통신하지 못했습니다. 잠시 후 다시 시도해주세요.");
    }

    return Promise.reject(error);
  },
);
